import React, { useState, useEffect } from 'react';
import { transportationApi } from '../services/transportationApi';
import TransportationForm from '../components/transportation/TransportationForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import {
  Transportation,
  CreateTransportationRequest,
  UpdateTransportationRequest,
  getTransportationTypeInfo
} from '../types/transportation';
import { LayoutGrid, List, Car, Pencil, Pause, Play, Trash2, Plus } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

/**
 * Transportation management page with CRUD operations
 */
const TransportationPage: React.FC = () => {
  const [transportations, setTransportations] = useState<Transportation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Form and dialog states
  const [showForm, setShowForm] = useState(false);
  const [editingTransportation, setEditingTransportation] = useState<Transportation | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  // Delete confirmation
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [transportationToDelete, setTransportationToDelete] = useState<Transportation | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast notifications
  const { toasts, removeToast, showSuccess, showError } = useToast();

  useEffect(() => {
    loadTransportations();
  }, [showInactive]);

  const loadTransportations = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await transportationApi.getAllTransportations(showInactive);
      setTransportations(data);
      showSuccess('Verkehrsmittel erfolgreich geladen');
    } catch (error) {
      console.error('Error loading transportations:', error);
      setError('Fehler beim Laden der Verkehrsmittel');
      showError('Fehler beim Laden der Verkehrsmittel');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTransportation = async (data: CreateTransportationRequest) => {
    try {
      setFormLoading(true);
      await transportationApi.createTransportation(data);
      showSuccess('Verkehrsmittel erfolgreich erstellt');
      setShowForm(false);
      await loadTransportations();
    } catch (error) {
      console.error('Error creating transportation:', error);
      showError('Fehler beim Erstellen des Verkehrsmittels');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTransportation = async (data: UpdateTransportationRequest) => {
    if (!editingTransportation) return;
    
    try {
      setFormLoading(true);
      await transportationApi.updateTransportation(editingTransportation.id, data);
      showSuccess('Verkehrsmittel erfolgreich aktualisiert');
      setShowForm(false);
      setEditingTransportation(null);
      await loadTransportations();
    } catch (error) {
      console.error('Error updating transportation:', error);
      showError('Fehler beim Aktualisieren des Verkehrsmittels');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTransportation = (transportation: Transportation) => {
    setTransportationToDelete(transportation);
    setShowDeleteDialog(true);
  };

  const confirmDeleteTransportation = async () => {
    if (!transportationToDelete) return;

    try {
      setDeleteLoading(true);
      await transportationApi.deleteTransportation(transportationToDelete.id);
      showSuccess('Verkehrsmittel erfolgreich gelÃ¶scht');
      setShowDeleteDialog(false);
      setTransportationToDelete(null);
      await loadTransportations();
    } catch (error) {
      console.error('Error deleting transportation:', error);
      showError('Fehler beim LÃ¶schen des Verkehrsmittels');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleActive = async (transportation: Transportation) => {
    try {
      await transportationApi.toggleTransportationActive(transportation.id);
      showSuccess(`Verkehrsmittel ${transportation.isActive ? 'deaktiviert' : 'aktiviert'}`);
      await loadTransportations();
    } catch (error) {
      console.error('Error toggling transportation status:', error);
      showError('Fehler beim Ã„ndern des Status');
    }
  };

  const handleEditTransportation = (transportation: Transportation) => {
    setEditingTransportation(transportation);
    setShowForm(true);
  };

  // Filter transportations based on search term
  const filteredTransportations = transportations.filter(transportation =>
    transportation.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    transportation.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (transportation.description && transportation.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading">
          <div className="page-spinner"></div>
          <p>Lade Verkehrsmittel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={() => setShowForm(true)} title="Neues Verkehrsmittel erstellen">
            <Plus size={14} /> Neu
          </button>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Inaktive anzeigen
          </label>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Verkehrsmittel suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredTransportations.length} Gefunden · {transportations.filter(t => t.isActive).length} Aktiv · {transportations.filter(t => !t.isActive).length} Inaktiv</span>
        </div>
      </div>

      {error && (
        <div className="page-error">
          <p>{error}</p>
          <button onClick={loadTransportations}>Erneut versuchen</button>
        </div>
      )}

      {!loading && filteredTransportations.length === 0 && (
        <div className="page-empty-state">
          <Car size={32} />
          <h3>{searchTerm ? 'Keine Verkehrsmittel gefunden' : 'Noch keine Verkehrsmittel'}</h3>
          <p>{searchTerm
            ? 'Versuchen Sie einen anderen Suchbegriff oder passen Sie die Filter an.'
            : 'Erstellen Sie Ihr erstes Verkehrsmittel, um zu beginnen.'
          }</p>
          {!searchTerm && (
            <button className="page-toggle-btn page-btn-create" onClick={() => setShowForm(true)}>
              <Plus size={14} /> Erstes Verkehrsmittel erstellen
            </button>
          )}
        </div>
      )}

      {!loading && filteredTransportations.length > 0 && (
        <>
          {viewMode === 'cards' ? (
            <div className="page-cards-grid">
              {filteredTransportations.map((transportation) => {
                const typeInfo = getTransportationTypeInfo(transportation.type);
                return (
                  <div key={transportation.id} className="page-card">
                    <div className="page-card-header">
                      <h3>{typeInfo?.icon || <Car size={16} />} {transportation.name}</h3>
                      <span className="page-badge" style={{ backgroundColor: transportation.isActive ? '#28a745' : '#dc3545' }}>
                        {transportation.isActive ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </div>
                    <div className="page-card-body">
                      <span className="page-badge" style={{ backgroundColor: '#6c757d' }}>{typeInfo?.label || transportation.type}</span>
                      {transportation.description && <p>{transportation.description}</p>}
                      {transportation.costPerKm && <div><strong>Kosten/km:</strong> {transportation.costPerKm}â‚¬</div>}
                      {transportation.capacity && <div><strong>KapazitÃ¤t:</strong> {transportation.capacity}</div>}
                    </div>
                    <div className="page-card-footer">
                      <small>Erstellt: {new Date(transportation.createdAt).toLocaleDateString('de-DE')}</small>
                      <div className="page-card-actions">
                        <button className="page-btn-icon edit" onClick={() => handleEditTransportation(transportation)} title="Bearbeiten"><Pencil size={14} /></button>
                        <button className="page-btn-icon" onClick={() => handleToggleActive(transportation)} title={transportation.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                          {transportation.isActive ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button className="page-btn-icon delete" onClick={() => handleDeleteTransportation(transportation)} title="LÃ¶schen"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="page-data-panel">
              <table className="page-table">
                <thead>
                  <tr>
                    <th>Verkehrsmittel</th>
                    <th>Typ</th>
                    <th>Beschreibung</th>
                    <th>Kosten/km</th>
                    <th>KapazitÃ¤t</th>
                    <th>Status</th>
                    <th>Erstellt</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransportations.map((transportation) => {
                    const typeInfo = getTransportationTypeInfo(transportation.type);
                    return (
                      <tr key={transportation.id}>
                        <td>{typeInfo?.icon || <Car size={14} />} {transportation.name}</td>
                        <td><span className="page-badge" style={{ backgroundColor: '#6c757d' }}>{typeInfo?.label || transportation.type}</span></td>
                        <td>{transportation.description || '-'}</td>
                        <td>{transportation.costPerKm ? `${transportation.costPerKm}â‚¬` : '-'}</td>
                        <td>{transportation.capacity || '-'}</td>
                        <td>
                          <span className="page-badge" style={{ backgroundColor: transportation.isActive ? '#28a745' : '#dc3545' }}>
                            {transportation.isActive ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td>{new Date(transportation.createdAt).toLocaleDateString('de-DE')}</td>
                        <td>
                          <div className="page-table-actions">
                            <button className="page-btn-icon edit" onClick={() => handleEditTransportation(transportation)} title="Bearbeiten"><Pencil size={14} /></button>
                            <button className="page-btn-icon" onClick={() => handleToggleActive(transportation)} title={transportation.isActive ? 'Deaktivieren' : 'Aktivieren'}>
                              {transportation.isActive ? <Pause size={14} /> : <Play size={14} />}
                            </button>
                            <button className="page-btn-icon delete" onClick={() => handleDeleteTransportation(transportation)} title="LÃ¶schen"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Transportation Form Modal */}
      {showForm && (
        <TransportationForm
          transportation={editingTransportation || undefined}
          onSave={editingTransportation ? 
            (data: CreateTransportationRequest | UpdateTransportationRequest) => handleUpdateTransportation(data as UpdateTransportationRequest) : 
            (data: CreateTransportationRequest | UpdateTransportationRequest) => handleCreateTransportation(data as CreateTransportationRequest)
          }
          onCancel={() => {
            setShowForm(false);
            setEditingTransportation(null);
          }}
          loading={formLoading}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Verkehrsmittel lÃ¶schen"
        message={`MÃ¶chten Sie das Verkehrsmittel "${transportationToDelete?.name}" wirklich lÃ¶schen? Diese Aktion kann nicht rÃ¼ckgÃ¤ngig gemacht werden.`}
        confirmText="LÃ¶schen"
        cancelText="Abbrechen"
        onConfirm={confirmDeleteTransportation}
        onCancel={() => {
          setShowDeleteDialog(false);
          setTransportationToDelete(null);
        }}
        loading={deleteLoading}
        type="danger"
      />

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default TransportationPage;

